import { FC, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
} from 'components/common';
import { IApplicationsAnalystTableProps, tableColumnsNames } from './types';
import { FormattedMessage } from 'react-intl';
import { SortDirectionEnum } from '../../../legacy/shared-types';
import { useApplications } from 'queries/applications/useApplications';
import { getPageURL, PageURL } from 'router/pageURL';
import { useNavigate } from 'react-router-dom';
import { EditIcon } from 'components/common/Icons';
import { FormattedTableCell } from './FormattedTableCell';
import { useQueryClient } from '@tanstack/react-query';
import { applicationFormApi } from 'api/applicationForm';
import { parseApplicationRoutesAndSaveToStorage } from 'utils/localStorage';
import { ApplicationForListingDto } from './types';
import { allCapsToTitleCase } from 'common/helpers';

export const ApplicationsTable: FC<IApplicationsAnalystTableProps> = ({ searchInputValue }) => {
  const [order, setOrder] = useState<SortDirectionEnum>(SortDirectionEnum.DESC);
  const [orderBy, setOrderBy] = useState<keyof ApplicationForListingDto>('submittedAt');
  const { data: allApplications, isLoading } = useApplications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const formattedApplications = allApplications?.map((application: ApplicationForListingDto) => ({
    id: application.referenceNumber || application.id,
    applicant: application.applicant,
    coApplicant: application.coApplicant,
    status: <FormattedMessage id={`applicationStatus.${application.status}`} />,
    type: allCapsToTitleCase(application.type),
    createdAt: application.createdAt || application.submittedAt,
    submittedAt: application.submittedAt,
  }));

  const sortedRows = formattedApplications?.sort((a: any, b: any) => {
    if (a[orderBy] < b[orderBy]) {
      return order === SortDirectionEnum.ASC ? -1 : 1;
    }

    if (a[orderBy] > b[orderBy]) {
      return order === SortDirectionEnum.ASC ? 1 : -1;
    }
    return 0;
  });

  const filteredBySearchInputValue = sortedRows?.filter((row: ApplicationForListingDto) => {
    const rowValues = Object.values(row);
    return rowValues.some((value: any) => value?.toString()?.toLowerCase().includes(searchInputValue?.toLowerCase()));
  });

  const sortHandler = (column: keyof ApplicationForListingDto) => {
    const isAsc = order === SortDirectionEnum.ASC;
    const isSameColumn = orderBy === column;

    setOrder(isAsc && isSameColumn ? SortDirectionEnum.DESC : SortDirectionEnum.ASC);
    setOrderBy(column);
  };

  const applicationdDetailsPreloadHandler = async (applicationId: string | number) => {
    await queryClient.prefetchQuery(['application-in-progress', applicationId], () =>
      applicationFormApi.getApplicationInProgress(String(applicationId)),
    );
    const applicationInProgress = queryClient.getQueryData<Record<string, any>>([
      'application-in-progress',
      applicationId,
    ]);

    if (applicationInProgress) parseApplicationRoutesAndSaveToStorage(applicationInProgress);

    navigate(getPageURL(PageURL.APPLICATION_EDIT, { id: applicationId }));
  };

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table aria-label='collapsible table'>
          <TableHead>
            <TableRow>
              <TableCell />
              {tableColumnsNames.map(columnName => (
                <TableCell key={columnName}>
                  <TableSortLabel
                    data-value={columnName}
                    active={orderBy === columnName}
                    direction={order}
                    onClick={() => sortHandler(columnName as keyof ApplicationForListingDto)}
                  >
                    <FormattedMessage id={`application.${columnName}`} />
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody isLoading={isLoading} columnsNumber={tableColumnsNames.length}>
            {filteredBySearchInputValue?.length ? (
              filteredBySearchInputValue.map((row: ApplicationForListingDto, index: number) => (
                <TableRow key={index}>
                  <TableCell />
                  {Object.entries(row).map(([objectKey, value]: [string, any]) => (
                    <TableCell key={objectKey}>
                      <FormattedTableCell objectKey={objectKey} value={value} />
                    </TableCell>
                  ))}
                  <TableCell>
                    {!row.submittedAt && (
                      <button
                        className={'cursor-pointer border-0 bg-transparent'}
                        onClick={() => applicationdDetailsPreloadHandler(row.id)}
                      >
                        <EditIcon htmlColor={'grey'} />
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell sx={{ textAlign: 'center' }} colSpan={9}>
                  <FormattedMessage id={'common.itemsNotFound'} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
